import { V2Event, V2_EVENT_TYPES } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/events';
import { injectable, inject } from 'inversify';
import { ClientEventQueryResult, ClientEventTables } from './db-wrapper-dexie';
import { timestampFactory as _timestampFactory } from '../../util/time';
import { random as _random } from '../../util/random';
import { DI_CONSTANTS, DI_TYPES } from '../../di-types';
import { ClientEventQueryV1 } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/client-event-query-schema';
import type { ConditionalExpressionProvider } from '../../util/conditional-expressions-provider';
import {
  CachedBindings,
  QueryResolver,
} from '@jetfabric/conditional-expressions/lib/data-reference-resolver';
import { Either } from '@jetfabric/conditional-expressions/lib/either';
import { QueryClientEvent } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/queries';
import { TraceState } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/trace';

// This wrapper class around ClientEventTables is necessary for inversifyJS to inject the
// wrapped class (ClientEventTables) which must inherit from Dexie (non-injectable).
@injectable()
export class ClientEventDB {
  private db: Promise<ClientEventTables>;

  constructor(
    @inject(DI_TYPES.TimestampFactory) timestampFactory: typeof _timestampFactory,
    @inject(DI_TYPES.random) random: typeof _random,
    @inject(DI_TYPES.ConditionaExpressionProvider)
    conditionalExpressionProvider: ConditionalExpressionProvider,
    @inject(DI_CONSTANTS.STORAGE_KEY_PREFIX) storageKeyPrefix: string,
    @inject(DI_CONSTANTS.CLIENT_EVENTS_MAX_COUNT) maxEventCount: number
  ) {
    this.db = conditionalExpressionProvider().then(evaluate => {
      return import(/* webpackChunkName: "db-wrapper-dexie" */ './db-wrapper-dexie').then(
        ({ ClientEventTables }) =>
          new ClientEventTables(storageKeyPrefix, timestampFactory, random, maxEventCount, evaluate)
      );
    });
  }

  async add(event: V2Event): Promise<number | undefined> {
    const db = await this.db;
    return await db.add(event);
  }

  async query(
    query: QueryClientEvent,
    queryResolver: QueryResolver,
    bindingsStack: CachedBindings[],
    traceState?: TraceState
  ): Promise<Either<QueryClientEvent, ClientEventQueryResult>> {
    return await (await this.db).query(query, queryResolver, bindingsStack, traceState);
  }

  async close() {
    return await (await this.db).close();
  }

  async delete() {
    await this.close();
    return await (await this.db).delete();
  }
}
